/**
 * Slide Element Types
 * Type definitions for spatial elements on slides
 */

import * as Y from 'yjs';

/**
 * Default font family for text elements
 */
export const DEFAULT_FONT_FAMILY = 'Arial';
export const DEFAULT_FONT_SIZE = 24;
export const DEFAULT_LINE_HEIGHT = 1.5;

/**
 * Base slide element (loaded from Y.js)
 */
export interface SlideElement {
  id: string;
  type: 'slide-text' | 'slide-title' | 'slide-subtitle' | 'slide-bullet-list' | 'slide-numbered-list' | 'slide-image' | 'slide-shape' | 'slide-table';
  position: ElementPosition;
  style?: ElementStyle;
  yElement: Y.XmlElement; // Reference to Y.js element
}

/**
 * Position of element on slide
 */
export interface ElementPosition {
  type: 'absolute';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  z?: number;
}

/**
 * Style properties
 */
export interface ElementStyle {
  fontSize?: number;
  fontWeight?: number | string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  backgroundColor?: string;
  lineHeight?: number;
  padding?: number;
  borderRadius?: number;
  border?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

/**
 * Text element with Y.Text content
 */
export interface TextElement extends SlideElement {
  type: 'slide-text' | 'slide-title' | 'slide-subtitle' | 'slide-bullet-list' | 'slide-numbered-list';
  content: Y.Text; // Simple Y.Text for contentEditable editor (replaced ProseMirror)
}

/**
 * Image element
 */
export interface ImageElement extends SlideElement {
  type: 'slide-image';
  src: string;
  alt?: string;
}

/**
 * Shape element
 */
export interface ShapeElement extends SlideElement {
  type: 'slide-shape';
  shapeType: 
    | 'rectangle' | 'circle' | 'triangle'      // Basic shapes
    | 'line' | 'arrow'                          // Lines
    | 'diamond' | 'roundedRect' | 'ellipse'     // Variants
    | 'pentagon' | 'hexagon' | 'star'           // Polygons
    | 'trapezoid' | 'parallelogram'             // Quadrilaterals
    | 'rightTriangle'                           // Triangle variants
    | 'custom';                                 // Custom SVG
  path?: string; // SVG path data for custom shapes
  // ✅ COORDINATE SYSTEM: Line/arrow endpoints stored RELATIVE to element position
  // These are internal storage coords, NOT the absolute coords AI provides via start/end
  x1?: number;  // Relative to position.x
  y1?: number;  // Relative to position.y
  x2?: number;  // Relative to position.x
  y2?: number;  // Relative to position.y
}

/**
 * Formatting range for AI
 */
export interface FormattingRange {
  start: number;
  end: number;
  marks: {
    strong?: boolean;
    em?: boolean;
    underline?: boolean;
    textColor?: string;
    fontSize?: number;
  };
}

/**
 * Element creation params
 */
export interface CreateElementParams {
  type: SlideElement['type'];
  position: ElementPosition;
  style?: ElementStyle;
  text?: string;
  formatting?: FormattingRange[];
  src?: string; // For images
  fileName?: string; // For images
  shapeType?: ShapeElement['shapeType'];
  path?: string; // For custom shapes
  // ✅ COORDINATE SYSTEM: Line/arrow endpoints (RELATIVE to position for storage)
  x1?: number;  // Relative to position.x
  y1?: number;  // Relative to position.y
  x2?: number;  // Relative to position.x
  y2?: number;  // Relative to position.y
}
